import React, { useEffect } from 'react'
import { Button, Form, Modal, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { formatAsDate, parseDate, toFormDate } from '../../../config/dayjsConfig'
import { Loan } from '../types/Loan'
import { DatePicker } from '../../../components/Form'
import { FormattedInputNumber } from '../../../components/Misc/FormattedInputNumber'
import { transformFieldData } from '../../../utils/form'
import { useUpdateLoan } from '../queries/useUpdateLoan'
import loanQueryKeys from '../queries/loanQueryKeys'

interface LoanInterestProps {
  loan: Loan
  handleOk: () => void
  isLocked?: boolean
}

const LoanInterestModal: React.FC<LoanInterestProps> = ({ loan, handleOk, isLocked = false }: LoanInterestProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const updateLoan = useUpdateLoan()

  useEffect(() => {
    form.resetFields()
    // Convert ISO dates to YYYY-MM-DD format to avoid timezone issues
    const interestRatesWithConvertedDates = loan?.interestRates
      .sort((a, b) => (parseDate(a?.date)?.valueOf() || 0) - (parseDate(b?.date)?.valueOf() || 0))
      .map(i => ({
        ...i,
        date: toFormDate(i.date)
      }))

    form.setFieldsValue({
      interestRates: interestRatesWithConvertedDates
    })
  }, [loan])

  const handleSubmit = async () => {
    try {
      if (!loan) return
      const formValues = await form.validateFields()
      const formattedValues = transformFieldData(formValues)

      // TODO: trvitseeko eteen käteen muunnella dataa, voiko loan tablessa Purposen purposena
      const values = {
        ...loan,
        purposes: loan.purposes || [],
        ...formattedValues,
        interestRates: formattedValues.interestRates?.map((r: any) => ({
          ...r,
          date: formatAsDate(r.date)
        }))
      }

      await updateLoan.mutateAsync(values)
      queryClient.invalidateQueries({
        queryKey: loanQueryKeys.summaries()
      })

      handleOk()
    } catch (info) {
      console.error('Validate Failed:', info)
    }
  }

  return (
    <Modal
      destroyOnHidden
      open={!!loan}
      width={700}
      title={t('loans:changing-interest')}
      okText={t('global:save')}
      cancelText={t('global:cancel')}
      maskClosable={false}
      confirmLoading={updateLoan.isPending}
      onCancel={() => {
        form.resetFields()
        handleOk()
      }}
      onOk={isLocked ? undefined : handleSubmit}
      okButtonProps={{ style: { display: isLocked ? 'none' : undefined } }}
    >
      <Form form={form} name="interest_rates" style={{ maxWidth: 700 }} autoComplete="off" disabled={isLocked}>
        <Form.List name="interestRates">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <Space key={field.key} align="baseline">
                  <Form.Item
                    label={t('contractsPage:date')}
                    name={[field.name, 'date']}
                    rules={[{ required: true, message: t('global:required-field') }]}
                  >
                    <DatePicker format="DD.MM.YYYY" />
                  </Form.Item>

                  <Form.Item
                    label={t('loans:interest-rate')}
                    name={[field.name, 'interestRate']}
                    rules={[{ required: true, message: t('global:required-field') }]}
                  >
                    <FormattedInputNumber step={1} precision={4} max={100} percentage addonAfter="%" />
                  </Form.Item>

                  {!isLocked && <MinusCircleOutlined onClick={() => remove(field.name)} />}
                </Space>
              ))}

              {!isLocked && (
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    {`${t('global:add')} ${t('loans:interestRateChange')}`}
                  </Button>
                </Form.Item>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  )
}

export default LoanInterestModal
